import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useMode } from '../contexts/ModeContext';
import { AppShell } from '../components/AppShell';
import { QuickAddFAB } from '../components/QuickAddFAB';
import { Button } from '../components/ui/button';
import { Plus } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { DashboardStatsGrid, DashboardRecentActivity, DashboardQuickActions } from '../components/dashboard/DashboardWidgets';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function DashboardPage() {
  const { user } = useAuth();
  const { isOPC, isCSPS } = useMode();
  const [stats, setStats] = useState(null);
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, chantiersRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/chantiers`, { withCredentials: true })
      ]);
      setStats(statsRes.data || {});
      setChantiers((chantiersRes.data || []).filter(c => c && c.id));
    } catch (_err) {
      toast.error('Erreur chargement tableau de bord');
    } finally {
      setLoading(false);
    }
  }, [setStats, setChantiers, setLoading]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open': return <span className="chantix-badge chantix-badge-warning">En cours</span>;
      case 'closed': return <span className="chantix-badge chantix-badge-success">Levee</span>;
      default: return <span className="chantix-badge chantix-badge-neutral">{status}</span>;
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high': return <span className="chantix-badge chantix-badge-error">Haute</span>;
      case 'medium': return <span className="chantix-badge chantix-badge-warning">Moyenne</span>;
      case 'low': return <span className="chantix-badge chantix-badge-success">Basse</span>;
      default: return <span className="chantix-badge chantix-badge-neutral">{priority}</span>;
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-10 h-10 animate-spin text-[#0055FF]" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-6" data-testid="dashboard-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#111827]" data-testid="dashboard-welcome">
              Bonjour, {user?.name?.split(' ')[0] || 'Utilisateur'}
            </h1>
            <p className="text-[#4B5563] mt-1">
              {isCSPS ? 'Tableau de bord Sécurité & Protection sante' : 'Voici un résumé de vos chantiers'}
            </p>
          </div>
          <Link to={isCSPS ? '/csps/chantiers' : '/chantiers/nouveau'}>
            <Button className={isCSPS ? 'bg-[#FF6B00] hover:bg-orange-700' : 'bg-[#0055FF] hover:bg-blue-700'} data-testid="new-chantier-btn">
              <Plus size={20} className="mr-2" />
              {isCSPS ? 'Chantiers CSPS' : 'Nouveau chantier'}
            </Button>
          </Link>
        </div>

        <DashboardStatsGrid stats={stats} isCSPS={isCSPS} />
        <DashboardRecentActivity stats={stats} isCSPS={isCSPS} getStatusBadge={getStatusBadge} getPriorityBadge={getPriorityBadge} />
        <DashboardQuickActions isCSPS={isCSPS} />
      </div>
      <QuickAddFAB chantiers={chantiers} />
    </AppShell>
  );
}
